import { api } from '@/config/api';

export type WorkflowTrigger =
  | 'lead.created'
  | 'lead.status_changed'
  | 'message.received'
  | 'deal.created'
  | 'deal.stage_changed'
  | 'document.paid';

export type WorkflowActionKind =
  | 'send_template'
  | 'create_task'
  | 'assign_owner'
  | 'update_field'
  | 'send_webhook'
  | 'delay'
  | 'condition'
  | 'branch';

export interface WorkflowAction {
  kind: WorkflowActionKind;
  config: Record<string, unknown>;
}

export type WorkflowConditionOp = 'eq' | 'ne' | 'in' | 'notIn' | 'startsWith' | 'contains' | 'gt' | 'lt' | 'exists' | 'notExists';

export interface WorkflowCondition {
  field: string;
  op: WorkflowConditionOp;
  value?: unknown;
}

export interface WorkflowConditionTree { all?: WorkflowCondition[]; any?: WorkflowCondition[] }

export interface WorkflowRule {
  ruleUid: string;
  companyUid: string;
  name: string;
  description?: string;
  isActive: boolean;
  trigger: WorkflowTrigger;
  conditions: WorkflowConditionTree;
  actions: WorkflowAction[];
  timesFired: number;
  lastFiredAt?: string;
  lastErrorAt?: string;
  lastErrorMessage?: string;
  createdAt: string;
}

export interface CreateRuleInput {
  name: string;
  description?: string;
  trigger: WorkflowTrigger;
  conditions?: WorkflowConditionTree;
  actions: WorkflowAction[];
  isActive?: boolean;
}

export type WorkflowRunStatus = 'running' | 'waiting' | 'succeeded' | 'failed' | 'interrupted';

export interface WorkflowRunStep {
  kind: string;
  status: 'succeeded' | 'failed' | 'skipped' | 'waiting' | 'stopped';
  message?: string;
  at: string;
}

export interface WorkflowRun {
  runUid: string;
  ruleUid: string;
  ruleName?: string;
  trigger: string;
  status: WorkflowRunStatus;
  leadUid?: string;
  dealUid?: string;
  subject?: string;
  steps: WorkflowRunStep[];
  failedSteps: number;
  waitingUntil?: string;
  finishedAt?: string;
  createdAt: string;
}

const BASE = '/slighter_crm/workflows';

export const workflowsService = {
  list: async (): Promise<WorkflowRule[]> => (await api.get(BASE)).data,
  get: async (uid: string): Promise<WorkflowRule> => (await api.get(`${BASE}/${uid}`)).data,
  create: async (dto: CreateRuleInput): Promise<WorkflowRule> => (await api.post(BASE, dto)).data,
  update: async (uid: string, dto: Partial<CreateRuleInput>): Promise<WorkflowRule> =>
    (await api.patch(`${BASE}/${uid}`, dto)).data,
  toggle: async (uid: string, isActive: boolean): Promise<WorkflowRule> =>
    (await api.post(`${BASE}/${uid}/toggle`, { isActive })).data,
  remove: async (uid: string): Promise<{ ok: boolean }> => (await api.delete(`${BASE}/${uid}`)).data,
  runs: async (q: { ruleUid?: string; status?: WorkflowRunStatus; limit?: number; before?: string } = {}): Promise<{ runs: WorkflowRun[]; hasMore: boolean }> =>
    (await api.get(`${BASE}/runs`, { params: q })).data,
  /** Dry run: which rules would match this payload and what they would do. Sends nothing. */
  testFire: async (trigger: WorkflowTrigger, payload: Record<string, unknown>): Promise<Array<{ ruleUid: string; name: string; isActive: boolean; matched: boolean; actions: string[] }>> =>
    (await api.post(`${BASE}/test-fire`, { trigger, payload })).data,
};
